import { useLtcComparisonScenarios } from '@client/components/ltc-financial-planning/hooks/useLtcComparisonScenarios';
import { cn } from '@client/lib';
import { formatCurrency } from '@shared';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import { Check, Shield, TrendingUp, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@client/components/ui/card';
import { StepContentProps } from '../types';
import { Badge } from '@client/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@client/components/ui/tooltip';

interface ComparisonStepProps extends StepContentProps {
  className?: string;
}

export function ComparisonStep({ className }: ComparisonStepProps) {
  const {
    projectedLtcCost,
    withoutInsurance,
    withInsurance,
    hasPolicy,
    netBenefit,
    insuranceIsBetter,
    benchmarkPolicyName,
  } = useLtcComparisonScenarios();

  // Hide remaining assets row if both are 0
  const showRemainingAssets =
    withoutInsurance.assetsRemaining > 0 || withInsurance.assetsRemaining > 0;

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Your Plan Summary</h1>
        <p className="mx-auto mt-3 max-w-2xl text-gray-700">
          See how your selected policy protects your assets and reduces out-of-pocket costs.
        </p>
      </div>

      {/* Plan Comparison Table */}
      <Card className="overflow-visible">
        <CardHeader className="rounded-t-xl bg-slate-50 pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">Plan Comparison</CardTitle>
            <div className="text-right">
              <p className="text-muted-foreground text-xs">Projected LTC Cost</p>
              <p className="text-foreground text-xl font-bold">
                <LtcCostDisplay value={projectedLtcCost} />
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="relative overflow-visible rounded-b-xl p-0">
          <div className="relative overflow-visible">
            <table className="w-full border-separate border-spacing-0">
              <colgroup>
                <col className="w-1/3" />
                <col className="w-1/3" />
                <col className="w-1/3" />
              </colgroup>

              {/* Column Headers */}
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-muted-foreground px-4 py-3 text-left text-sm font-medium"></th>
                  <th
                    className={cn(
                      'relative px-4 py-3 text-center align-bottom',
                      !hasPolicy && 'bg-amber-50/50 dark:bg-amber-950/20',
                    )}
                  >
                    {!hasPolicy && (
                      <div
                        className="pointer-events-none absolute inset-0 rounded-t-xl border-2 border-amber-400"
                        style={{ left: '-2px', right: '-2px', top: '-2px' }}
                      />
                    )}
                    {!hasPolicy && (
                      <span className="text-muted-foreground relative z-10 mb-1.5 inline-flex items-center gap-1 text-xs">
                        <Check className="h-3 w-3" />
                        Your Plan
                      </span>
                    )}
                    <span className="text-foreground relative z-10 block text-sm font-semibold">
                      Without Insurance
                    </span>
                  </th>
                  <th
                    className={cn(
                      'relative px-4 py-3 text-center align-bottom',
                      hasPolicy && 'bg-primary/5',
                    )}
                  >
                    {hasPolicy && (
                      <div
                        className="border-primary/60 pointer-events-none absolute inset-0 rounded-t-xl border-2"
                        style={{ left: '-2px', right: '-2px', top: '-2px' }}
                      />
                    )}
                    {hasPolicy && (
                      <span className="text-muted-foreground relative z-10 mb-1.5 inline-flex items-center gap-1 text-xs">
                        <Check className="h-3 w-3" />
                        Your Plan
                      </span>
                    )}
                    <div className="relative z-10 flex items-center justify-center gap-1.5">
                      <Shield className="text-primary h-4 w-4" />
                      <span className="text-primary text-sm font-semibold">With Insurance</span>
                      {!hasPolicy && benchmarkPolicyName && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3.5 w-3.5 cursor-help text-gray-700" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs text-sm font-normal">
                                Using <span className="font-semibold">{benchmarkPolicyName}</span>{' '}
                                as benchmark - the top ROI result for your profile
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>

              {/* Metrics Rows */}
              <tbody>
                {/* Row 1: LTC Coverage */}
                <tr className="bg-background border-b">
                  <td className="text-foreground font-regular px-4 py-3 text-sm">
                    Total LTC Coverage (%)
                  </td>
                  <td
                    className={cn(
                      'relative px-4 py-3 text-center',
                      !hasPolicy && 'bg-amber-50/30 dark:bg-amber-950/10',
                    )}
                  >
                    {!hasPolicy && (
                      <div
                        className="pointer-events-none absolute inset-0 border-l-2 border-r-2 border-amber-400"
                        style={{ left: '-2px', right: '-2px' }}
                      />
                    )}
                    <div className="relative z-10 flex items-center justify-center gap-2">
                      <span className="text-foreground text-base font-semibold tabular-nums">
                        {formatCurrency(withoutInsurance.ltcCoverage)}
                      </span>
                      <Badge
                        variant="outline"
                        className="bg-muted rounded-full px-2 py-0.5 text-xs font-semibold text-gray-700"
                      >
                        {withoutInsurance.coveragePercent}%
                      </Badge>
                    </div>
                  </td>
                  <td className={cn('relative px-4 py-3 text-center', hasPolicy && 'bg-primary/5')}>
                    {hasPolicy && (
                      <div
                        className="border-primary/60 pointer-events-none absolute inset-0 border-l-2 border-r-2"
                        style={{ left: '-2px', right: '-2px' }}
                      />
                    )}
                    <div className="relative z-10 flex items-center justify-center gap-2">
                      <span className="text-foreground text-base font-semibold tabular-nums">
                        {formatCurrency(withInsurance.ltcCoverage)}
                      </span>
                      <Badge
                        variant="outline"
                        className="bg-muted rounded-full px-2 py-0.5 text-xs font-semibold text-gray-700"
                      >
                        {withInsurance.coveragePercent}%
                      </Badge>
                    </div>
                  </td>
                </tr>

                {/* Row 2: Return on Investment (ROI) */}
                <tr className="bg-background border-b">
                  <td className="text-foreground font-regular px-4 py-3 text-sm">
                    Return on Investment (ROI)
                  </td>
                  <td
                    className={cn(
                      'relative px-4 py-3 text-center',
                      !hasPolicy && 'bg-amber-50/30 dark:bg-amber-950/10',
                    )}
                  >
                    {!hasPolicy && (
                      <div
                        className="pointer-events-none absolute inset-0 border-l-2 border-r-2 border-amber-400"
                        style={{ left: '-2px', right: '-2px' }}
                      />
                    )}
                    <span className="text-foreground relative z-10 text-base font-semibold tabular-nums">
                      {withoutInsurance.roi.toFixed(2)}%
                    </span>
                  </td>
                  <td className={cn('relative px-4 py-3 text-center', hasPolicy && 'bg-primary/5')}>
                    {hasPolicy && (
                      <div
                        className="border-primary/60 pointer-events-none absolute inset-0 border-l-2 border-r-2"
                        style={{ left: '-2px', right: '-2px' }}
                      />
                    )}
                    <span className="text-foreground relative z-10 text-base font-semibold tabular-nums">
                      {withInsurance.roi.toFixed(2)}%
                    </span>
                  </td>
                </tr>

                {/* Row 3: Coverage Gap (Out-of-Pocket) */}
                {(withoutInsurance.coverageGap > 0 || withInsurance.coverageGap > 0) && (
                  <tr className="bg-background border-b">
                    <td className="text-foreground font-regular px-4 py-3 text-sm">
                      Coverage Gap (Out-of-Pocket)
                    </td>
                    <td
                      className={cn(
                        'relative px-4 py-3 text-center',
                        !hasPolicy && 'bg-amber-50/30 dark:bg-amber-950/10',
                      )}
                    >
                      {!hasPolicy && (
                        <div
                          className="pointer-events-none absolute inset-0 border-l-2 border-r-2 border-amber-400"
                          style={{ left: '-2px', right: '-2px' }}
                        />
                      )}
                      <span className="text-foreground relative z-10 text-base font-semibold tabular-nums">
                        {withoutInsurance.coverageGap > 0
                          ? formatCurrency(withoutInsurance.coverageGap)
                          : '—'}
                      </span>
                    </td>
                    <td
                      className={cn('relative px-4 py-3 text-center', hasPolicy && 'bg-primary/5')}
                    >
                      {hasPolicy && (
                        <div
                          className="border-primary/60 pointer-events-none absolute inset-0 border-l-2 border-r-2"
                          style={{ left: '-2px', right: '-2px' }}
                        />
                      )}
                      <span className="text-foreground relative z-10 text-base font-semibold tabular-nums">
                        {withInsurance.coverageGap > 0
                          ? formatCurrency(withInsurance.coverageGap)
                          : '—'}
                      </span>
                    </td>
                  </tr>
                )}

                {/* Row 4: Additional Payout (Death Benefit) */}
                <tr className={cn('bg-background', showRemainingAssets ? 'border-b' : '')}>
                  <td className="text-foreground font-regular px-4 py-3 text-sm">
                    Additional Payout (e.g. Death Benefit)
                  </td>
                  <td
                    className={cn(
                      'relative px-4 py-3 text-center',
                      !hasPolicy && 'bg-amber-50/30 dark:bg-amber-950/10',
                    )}
                  >
                    {!hasPolicy && (
                      <div
                        className="pointer-events-none absolute inset-0 border-l-2 border-r-2 border-amber-400"
                        style={{
                          left: '-2px',
                          right: '-2px',
                        }}
                      />
                    )}
                    <span className="text-foreground relative z-10 text-base font-semibold tabular-nums">
                      {formatCurrency(withoutInsurance.nonLtcPayout)}
                    </span>
                  </td>
                  <td className={cn('relative px-4 py-3 text-center', hasPolicy && 'bg-primary/5')}>
                    {hasPolicy && (
                      <div
                        className="border-primary/60 pointer-events-none absolute inset-0 border-l-2 border-r-2"
                        style={{
                          left: '-2px',
                          right: '-2px',
                        }}
                      />
                    )}
                    <span className="text-foreground relative z-10 text-base font-semibold tabular-nums">
                      {formatCurrency(withInsurance.nonLtcPayout)}
                    </span>
                  </td>
                </tr>

                {/* Row 5: Remaining Assets After LTC */}
                {showRemainingAssets && (
                  <tr className="bg-background border-b">
                    <td className="text-foreground font-regular px-4 py-3 text-sm">
                      Remaining Assets after Projected LTC Event
                    </td>
                    <td
                      className={cn(
                        'relative px-4 py-3 text-center',
                        !hasPolicy && 'bg-amber-50/30 dark:bg-amber-950/10',
                      )}
                    >
                      {!hasPolicy && (
                        <div
                          className="pointer-events-none absolute inset-0 border-l-2 border-r-2 border-amber-400"
                          style={{ left: '-2px', right: '-2px' }}
                        />
                      )}
                      <span className="text-foreground relative z-10 text-base font-semibold tabular-nums">
                        {formatCurrency(withoutInsurance.assetsRemaining)}
                      </span>
                    </td>
                    <td
                      className={cn('relative px-4 py-3 text-center', hasPolicy && 'bg-primary/5')}
                    >
                      {hasPolicy && (
                        <div
                          className="border-primary/60 pointer-events-none absolute inset-0 border-l-2 border-r-2"
                          style={{ left: '-2px', right: '-2px' }}
                        />
                      )}
                      <span className="text-foreground relative z-10 text-base font-semibold tabular-nums">
                        {formatCurrency(withInsurance.assetsRemaining)}
                      </span>
                    </td>
                  </tr>
                )}

                {/* Footer Row: Net Benefit */}
                <tr className="bg-slate-50">
                  <td className="px-4 py-3"></td>
                  <td
                    className={cn(
                      'relative px-4 py-4 text-center',
                      !insuranceIsBetter && !hasPolicy && 'bg-amber-50/30 dark:bg-amber-950/10',
                    )}
                  >
                    {!insuranceIsBetter && !hasPolicy && (
                      <div
                        className="pointer-events-none absolute inset-0 rounded-b-xl border-b-2 border-l-2 border-r-2 border-amber-400"
                        style={{ left: '-2px', right: '-2px', bottom: '-2px' }}
                      />
                    )}
                    {!insuranceIsBetter && (
                      <div className="relative z-10">
                        <div className="text-muted-foreground mb-1 flex items-center justify-center gap-1.5 text-xs">
                          <TrendingUp className="h-3 w-3" />
                          <span>Net Benefit</span>
                        </div>
                        <div className="text-lg font-bold text-green-600">
                          +{formatCurrency(netBenefit)}
                        </div>
                        <div className="text-muted-foreground text-xs">more for LTC</div>
                      </div>
                    )}
                  </td>
                  <td className={cn('relative px-4 py-4 text-center', hasPolicy && 'bg-primary/5')}>
                    {hasPolicy && (
                      <div
                        className="border-primary/60 pointer-events-none absolute inset-0 rounded-b-xl border-b-2 border-l-2 border-r-2"
                        style={{ left: '-2px', right: '-2px', bottom: '-2px' }}
                      />
                    )}
                    {insuranceIsBetter && (
                      <div className="relative z-10">
                        <div className="text-muted-foreground mb-1 flex items-center justify-center gap-1.5 text-xs">
                          <TrendingUp className="h-3 w-3" />
                          <span>Net Benefit</span>
                        </div>
                        <div className="text-lg font-bold text-green-600">
                          +{formatCurrency(netBenefit)}
                        </div>
                        <div className="text-muted-foreground text-xs">more for LTC</div>
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
